import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import { useDispatch, useSelector } from 'react-redux';
import Transition from '../Transition/Transition';
import { closeModal } from './modalReducer';
import useStyles from 'styles/forms';
import { getInvoiceSettings, saveInvoiceSettings } from '../../store/actions/tenantsAction';

const InvoiceSettingsModal = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState({
    company_name: '',
    company_address: '',
    company_email: '',
    company_phone: '',
    invoice_number_prefix: 'INV',
    payment_terms: '',
    payment_instructions: '',
    footer_notes: '',
    invoice_date_configuration: 7
  });

  const settings = useSelector((state) => state.tenants.invoiceSettings.data);

  useEffect(() => {
    dispatch(getInvoiceSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      console.log(settings);
      setFormData({
        company_name: settings.companyName || '',
        company_address: settings.companyAddress || '',
        company_email: settings.companyEmail || '',
        company_phone: settings.companyPhone || '',
        invoice_number_prefix: settings.invoiceNumberPrefix || 'INV',
        payment_terms: settings.paymentTerms || '',
        payment_instructions: settings.paymentInstructions || '',
        footer_notes: settings.footerNotes || '',
        invoice_date_configuration: settings.invoiceDateConfiguration || 7
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleCloseModal = () => dispatch(closeModal());

  const handleSave = () => {
    dispatch(saveInvoiceSettings(formData))
      .then(() => dispatch(closeModal()))
      .catch((err) => console.error('Failed to save settings', err));
  };

  return (
    <Dialog
      open
      onClose={handleCloseModal}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Header Section */}
      <Box p={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Box bgcolor="#E8EFFF" p={1} borderRadius={2} display="flex" mr={2} color="#4A90E2">
            <SettingsIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Invoice Settings
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure your invoice template and automation
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleCloseModal}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Navigation Tabs */}
      <Box px={2} pt={1}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 'bold', minHeight: 48 } }}
        >
          <Tab
            icon={<BusinessIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Company Details"
          />
          <Tab
            icon={<DescriptionIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Invoice Template"
          />
          <Tab
            icon={<NotificationsIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label="Automation"
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ minHeight: '350px', py: 3 }}>
        {/* TAB 1: COMPANY DETAILS */}
        {tabValue === 0 && (
          <Box display="flex" flexDirection="column" gap={3}>
            <TextField
              fullWidth
              label="Company Name"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              helperText="This will appear at the top of your invoices"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Company Address"
              name="company_address"
              value={formData.company_address}
              onChange={handleChange}
            />
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Email"
                name="company_email"
                placeholder="accounts@company.com"
                value={formData.company_email}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Phone"
                name="company_phone"
                placeholder="+44 20 1234 5678"
                value={formData.company_phone}
                onChange={handleChange}
              />
            </Box>
          </Box>
        )}

        {/* TAB 2: INVOICE TEMPLATE */}
        {tabValue === 1 && (
          <Box display="flex" flexDirection="column" gap={3}>
            <TextField
              sx={{ maxWidth: '200px' }}
              label="Invoice Number Prefix"
              name="invoice_number_prefix"
              value={formData.invoice_number_prefix}
              onChange={handleChange}
              helperText="Example: INV-202412-001"
            />
            <TextField
              fullWidth
              label="Payment Terms"
              name="payment_terms"
              placeholder="Payment due within 7 days"
              value={formData.payment_terms}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Payment Instructions"
              name="payment_instructions"
              value={formData.payment_instructions}
              onChange={handleChange}
              helperText="Include bank details or payment link"
            />
            <TextField
              fullWidth
              label="Footer Notes"
              name="footer_notes"
              value={formData.footer_notes}
              onChange={handleChange}
            />
          </Box>
        )}

        {/* TAB 3: AUTOMATION */}
        {tabValue === 2 && (
          <Box p={2} border="1px solid #F0F0F0" borderRadius={2} bgcolor="#FAFAFF">
            <Box display="flex" alignItems="center" mb={1}>
              <DescriptionIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                Invoice Date Configuration
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" mb={3}>
              Set how many days before the rent period to date your invoices (applies to all
              invoices)
            </Typography>

            <Box display="flex" alignItems="center" gap={4}>
              <TextField
                type="number"
                label="Days before rent period"
                name="invoice_date_configuration"
                value={formData.invoice_date_configuration}
                onChange={handleChange}
                sx={{ width: '150px' }}
              />
              <Box bgcolor="white" p={2} borderRadius={2} border="1px solid #EEE">
                <Typography variant="caption" color="textSecondary">
                  Example:
                </Typography>
                <Typography variant="body2">
                  For January 2026 rent, invoice dated: <b>{getExampleDate(formData.invoice_date_configuration)}</b>
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseModal} sx={{ textTransform: 'none', color: 'text.primary' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ textTransform: 'none', px: 4, borderRadius: 2, bgcolor: '#3B82F6' }}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceSettingsModal;

const getExampleDate = (days) => {
  const referenceDate = new Date(2026, 0, 1);
  const numDays = parseInt(days, 10) || 0;

  referenceDate.setDate(referenceDate.getDate() - numDays);

  return referenceDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};